import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { fleetHashTab } from '@/services/claude-fleet'
import type { NavigationItem } from '@/utils/navigationFilter'

/** Pages with no nav entry of their own that still belong to a group, which stays open on them. */
const GROUP_PAGES: Record<string, string[]> = { Claude: ['/admin/tools/tickets/'] }

/** Whether a group entry starts a new section, so its heading is drawn above it. */
export function startsSection(children: NavigationItem[], index: number): boolean {
  const section = children[index]?.section
  return Boolean(section) && children[index - 1]?.section !== section
}

/** Where a group entry goes: its page, or its tab on that page. */
export function childTarget(child: NavigationItem) {
  return child.hash ? { path: child.to ?? '/', hash: child.hash } : child.to ?? '/'
}

/** The sidebar's groups: which one is open, and which of its entries is current. */
export function useSidebarGroups() {
  const route = useRoute()

  /**
   * The group the person opened (null: they closed them all). Until they choose, the group holding the
   * current page is open, and choosing again starts over on the next navigation.
   */
  const chosenGroup = ref<string | null | undefined>(undefined)

  function hasActiveChild(item: NavigationItem): boolean {
    if (!item.children) return false
    if (GROUP_PAGES[item.title]?.some(path => route.path.startsWith(path))) return true
    return item.children.some(child => child.to && route.path.startsWith(child.to))
  }

  /** A tab entry is current only while its tab is open; the page with no tab open is on its first tab. */
  function isChildActive(child: NavigationItem, isActive: boolean): boolean {
    if (!child.hash) return isActive
    return route.path === child.to && `#${fleetHashTab(route.hash)}` === child.hash
  }

  function isGroupOpen(item: NavigationItem): boolean {
    return chosenGroup.value === undefined ? hasActiveChild(item) : chosenGroup.value === item.title
  }

  function toggleGroup(item: NavigationItem): void {
    chosenGroup.value = isGroupOpen(item) ? null : item.title
  }

  function resetGroups(): void {
    chosenGroup.value = undefined
  }

  watch(() => route.path, resetGroups)

  return { hasActiveChild, isGroupOpen, toggleGroup, isChildActive, resetGroups }
}
